import React, { Fragment } from 'react'
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel,
    Box,
    useTheme,
} from '@mui/material'
import RequestAccessButton from './RequestAccessButton'
import { Loader, Tooltip } from 'packages/eid-ui'
import TextFormatter from './TextFormatter'
import { useTranslation } from 'react-i18next'
import DynamicComponents from 'components/DynamicComponents'
import { ResourceListingInfoIcon } from 'components/DynamicComponents/ResourceListingInfoIcon'
import ActivateNowButton from './ActivateNowButton'
import { defaultDataProtectedSubcomponent } from 'utils'

const maxCharacters = 80

const tooltipStyles = {
    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
    padding: '8px',
    maxWidth: '500px',
}

var getValue = (ci, a) => {
    // QUICK Fix not to show unknown in computers
    if (a.name === 'infrastructureName' && ci[a.name] === 'Unknown') {
        return '-'
    }
    return ci[a.name] ?? ''
}

function getRequestAccessTableRowLabel(item, filteredAttributes) {
    const header = filteredAttributes.find((a) => a.isHeader)
    if (header) {
        const v = getValue(item, header)
        const s = String(v ?? '').trim()
        if (s && s !== '-') return s
    }
    const row = item.resource ?? item
    if (row?.friendlyName) return String(row.friendlyName).trim()
    if (row?.name) return String(row.name).trim()
    const id = item.id ?? row?.id
    return id != null ? `Row ${id}` : 'Table row'
}

const EXPLAIN_GRID_HINT_MAX = 260

function buildRequestAccessCellExplain(rowLabel, columnLabel, attribute, item) {
    let detail = ''
    if (attribute.component) {
        detail = 'Interactive control in this cell'
    } else if (attribute.type === 'array') {
        detail = 'Linked or list values in this cell'
    } else {
        const raw = String(getValue(item, attribute) ?? '')
            .replace(/\s+/g, ' ')
            .trim()
        detail =
            raw.length > 100 ? `${raw.slice(0, 97)}…` : raw || '(empty)'
    }
    const hint = `Catalog list · Row: ${rowLabel} · Column: ${columnLabel} · ${detail}`
    return hint.length > EXPLAIN_GRID_HINT_MAX
        ? `${hint.slice(0, EXPLAIN_GRID_HINT_MAX - 1)}…`
        : hint
}

const renderComponent = (attribute, data) => {
    const DynamicComponentToRender = DynamicComponents[attribute.component.name]

    return <DynamicComponentToRender attribute={attribute} data={data} />
}

const TableView = ({
    fallbackMessage,
    data,
    attributes,
    onItemClick,
    loading,
    onSort,
    sortBy,
    sortOrder,
    currentResourceType,
}) => {
    const { t } = useTranslation()
    const toolTipValue = (items) => {
        const newItems = []
        items.map((item) => newItems.push(item.friendlyName))
        return newItems.join(`, `)
    }

    const filteredAttributes = attributes.filter(
        (a) => !a.manageAccessOnlyAttribute,
    )
    const headings = filteredAttributes.map((a) => ({
        ...a,
        label: t(a.label),
        sort: a.sortable ? a.sortable : false,
    }))

    const theme = useTheme()

    const handleSort = (data) => {
        const sortingProperty =
            data.columnName && data.columnName.length > 0
                ? data.columnName
                : data.name

        onSort(
            data,
            sortBy !== sortingProperty
                ? 'desc'
                : !sortOrder || sortOrder === '' || sortOrder === 'asc'
                ? 'desc'
                : 'asc',
        )
    }

    return (
        <>
            <Table
                id="identities_list_table"
                sx={{
                    backgroundColor: theme?.palette?.background?.paper,
                    '& tr:hover': {
                        backgroundColor: '#f7f8fa',
                    },
                }}
            >
                <TableHead variant="eidHead">
                    <TableRow
                        sx={{
                            border: 'solid 1px #efeff1',
                        }}
                    >
                        {headings.map((heading, index) => {
                            const sortingProperty =
                                heading.columnName &&
                                heading.columnName.length > 0
                                    ? heading.columnName
                                    : heading.name

                            return (
                                <TableCell
                                    key={'pd-head-cell-' + index}
                                    data-ai-explain={`Column: ${heading.label || heading.name}`}
                                    sx={{
                                        width: 'auto',
                                        overflow: 'hidden',
                                        padding: '15px 10px',
                                        borderBottom: '0',
                                        '&:first-child': {
                                            paddingLeft: '20px',
                                        },
                                        '&:last-child': {
                                            paddingRight: '20px',
                                        },
                                        maxWidth: '200px',
                                        overflowWrap: 'break-word',
                                        wordBreak: 'break-word',
                                        lineHeight: '15px',
                                        fontSize: '13px !important',
                                        '& svg': {
                                            margin: '0px !important',
                                        },
                                    }}
                                    align={heading.align}
                                    style={heading.headerCellStyles}
                                    data-protectedsubcomponent={
                                        heading?.requireAccess?.control ||
                                        defaultDataProtectedSubcomponent
                                    }
                                >
                                    {!heading.sort ? (
                                        <span style={{ display: 'flex' }}>
                                            {heading.label}
                                        </span>
                                    ) : (
                                        <span style={{ display: 'flex' }}>
                                            <Tooltip
                                                title={
                                                    sortBy ===
                                                        sortingProperty &&
                                                    sortOrder === 'desc'
                                                        ? t(
                                                              'Common_SortAscending',
                                                          )
                                                        : t(
                                                              'Common_SortDescending',
                                                          )
                                                }
                                            >
                                                <TableSortLabel
                                                    active={
                                                        sortBy ===
                                                        sortingProperty
                                                    }
                                                    direction={
                                                        sortBy ===
                                                        sortingProperty
                                                            ? sortOrder
                                                            : 'asc'
                                                    }
                                                    sx={{
                                                        '& svg': {
                                                            marginLeft:
                                                                '4px !important',
                                                        },
                                                    }}
                                                    style={{
                                                        color:
                                                            sortBy ===
                                                                sortingProperty &&
                                                            'yellow !important',
                                                    }}
                                                    onClick={() =>
                                                        handleSort(heading)
                                                    }
                                                >
                                                    {heading.label}
                                                </TableSortLabel>
                                            </Tooltip>
                                        </span>
                                    )}
                                </TableCell>
                            )
                        })}
                        <TableCell
                            data-ai-explain="Actions column"
                            sx={{
                                width: 'auto',
                                padding: '15px 10px',
                                borderBottom: '0',
                                '&:first-child': {
                                    paddingLeft: '20px',
                                },
                                '&:last-child': {
                                    paddingRight: '20px',
                                },
                                maxWidth: '200px',
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word',
                                overflow: 'hidden',
                                '& svg': {
                                    margin: '0px !important',
                                },
                            }}
                        >
                            <span
                                style={{
                                    display: 'flex',
                                    justifySelf: 'center',
                                }}
                            >
                                {t('Actions')}
                            </span>
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {loading && (
                        <TableRow>
                            <TableCell
                                colSpan={headings.length + 1}
                                style={{ textAlign: 'center' }}
                            >
                                <Loader />
                            </TableCell>
                        </TableRow>
                    )}
                    {data &&
                        (data.length === 0 ? (
                            <TableRow
                                data-ai-explain="Empty table: no catalog items match"
                                sx={{
                                    border: 'solid 1px #efeff1',
                                }}
                            >
                                <TableCell
                                    colSpan={headings.length + 1}
                                    style={{ textAlign: 'center' }}
                                >
                                    {fallbackMessage}
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {data.map((item) => {
                                    const showActivateNow =
                                        item.canActivateNow &&
                                        currentResourceType.name !== 'Computers'
                                    const rowLabel = getRequestAccessTableRowLabel(
                                        item,
                                        filteredAttributes,
                                    )
                                    return (
                                        <TableRow
                                            key={'pd-body-row' + item.id}
                                            sx={{
                                                border: 'solid 1px #efeff1',
                                            }}
                                        >
                                            {filteredAttributes.map((a) => {
                                                let wrappedCellContent

                                                if (a.component) {
                                                    wrappedCellContent =
                                                        renderComponent(a, item)
                                                } else if (a.type !== 'array') {
                                                    const cellContent = (
                                                        <Box
                                                            maxHeight="200px"
                                                            display="flex"
                                                            overflow="auto"
                                                            minHeight="50px"
                                                            style={{
                                                                display: 'flex',
                                                                wordBreak:
                                                                    'break-word',
                                                                wordWrap:
                                                                    'break-word',
                                                                alignItems:
                                                                    'center',
                                                                ...a.style,
                                                            }}
                                                        >
                                                            <div>
                                                                {getValue(
                                                                    item,
                                                                    a,
                                                                ).length >
                                                                maxCharacters
                                                                    ? `${getValue(
                                                                          item,
                                                                          a,
                                                                      ).substring(
                                                                          0,
                                                                          maxCharacters,
                                                                      )}...`
                                                                    : getValue(
                                                                          item,
                                                                          a,
                                                                      )}{' '}
                                                            </div>
                                                        </Box>
                                                    )
                                                    wrappedCellContent =
                                                        getValue(item, a)
                                                            .length >
                                                        maxCharacters ? (
                                                            <Tooltip
                                                                title={getValue(
                                                                    item,
                                                                    a,
                                                                )}
                                                                color="#5D6870"
                                                                fontColor={
                                                                    theme
                                                                        .palette
                                                                        .common
                                                                        .white
                                                                }
                                                                enterDelay={500}
                                                                enterNextDelay={
                                                                    500
                                                                }
                                                                disableInteractive={
                                                                    false
                                                                }
                                                                tooltipStyles={
                                                                    tooltipStyles
                                                                }
                                                            >
                                                                {cellContent}
                                                            </Tooltip>
                                                        ) : (
                                                            cellContent
                                                        )
                                                } else {
                                                    wrappedCellContent = (
                                                        <TextFormatter
                                                            style={{
                                                                textDecoration:
                                                                    'underline',
                                                            }}
                                                            value={toolTipValue(
                                                                getValue(
                                                                    item,
                                                                    a,
                                                                ),
                                                            )}
                                                        />
                                                    )
                                                }
                                                return (
                                                    <TableCell
                                                        key={`${item.id}${a.name}`}
                                                        data-ai-explain={buildRequestAccessCellExplain(
                                                            rowLabel,
                                                            t(a.label),
                                                            a,
                                                            item,
                                                        )}
                                                        data-ai-explain-section="listing"
                                                        sx={{
                                                            maxWidth: '250px',
                                                            width: 'auto',
                                                            overflow: 'hidden',
                                                            padding:
                                                                '15px 10px',
                                                            fontSize: '13px',
                                                            borderBottom: '0',
                                                            '&:first-child': {
                                                                paddingLeft:
                                                                    '20px',
                                                            },
                                                            '&:last-child': {
                                                                paddingRight:
                                                                    '20px',
                                                            },
                                                        }}
                                                    >
                                                        {a.moreInfoIcon ? (
                                                            <Box display="flex">
                                                                {
                                                                    wrappedCellContent
                                                                }
                                                                <ResourceListingInfoIcon
                                                                    attribute={
                                                                        a
                                                                    }
                                                                    data={item}
                                                                />
                                                            </Box>
                                                        ) : (
                                                            wrappedCellContent
                                                        )}
                                                    </TableCell>
                                                )
                                            })}
                                            <TableCell
                                                sx={{
                                                    maxWidth: '250px',
                                                    width: 'auto',
                                                    overflow: 'hidden',
                                                    padding: '15px 10px',
                                                    fontSize: '13px',
                                                    borderBottom: '0',
                                                    '&:first-child': {
                                                        paddingLeft: '20px',
                                                    },
                                                    '&:last-child': {
                                                        paddingRight: '20px',
                                                    },
                                                }}
                                            >
                                                <Box
                                                    display="flex"
                                                    flexDirection="row"
                                                    justifyContent="flex-end"
                                                    width={'100%'}
                                                    sx={{
                                                        '& button': {
                                                            borderRadius: '4px',
                                                        },
                                                    }}
                                                >
                                                    {showActivateNow ? (
                                                        <>
                                                            <RequestAccessButton
                                                                onClick={() =>
                                                                    onItemClick(
                                                                        item,
                                                                    )
                                                                }
                                                                label={t(
                                                                    'Common_Details',
                                                                )}
                                                                data-ai-explain="Open details for this catalog row"
                                                            />

                                                            <ActivateNowButton
                                                                resource={item}
                                                                resourceType={
                                                                    currentResourceType
                                                                }
                                                                data-ai-explain="Activate this resource now (immediate provisioning)"
                                                            />
                                                        </>
                                                    ) : (
                                                        <RequestAccessButton
                                                            onClick={() =>
                                                                onItemClick(
                                                                    item,
                                                                )
                                                            }
                                                            data-ai-explain="Request access for this catalog row"
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </>
                        ))}
                </TableBody>
            </Table>
        </>
    )
}

export default TableView
